import { useState, useEffect, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Printer, Shield, FileText, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ui/theme-toggle';

export interface TocItem {
  id: string;
  title: string;
}

interface LegalPageLayoutProps {
  badge: string;
  title: string;
  lastUpdated: string;
  tocItems: TocItem[];
  children: ReactNode;
}

export function LegalPageLayout({
  badge,
  title,
  lastUpdated,
  tocItems,
  children,
}: LegalPageLayoutProps) {
  const [activeSection, setActiveSection] = useState<string>(tocItems[0]?.id || '');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (let i = tocItems.length - 1; i >= 0; i--) {
        const item = tocItems[i];
        if (!item) continue;
        const element = document.getElementById(item.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(item.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background text-foreground antialiased selection:bg-primary/20 selection:text-primary transition-colors">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-b border-border/80 dark:border-white/10 transition-colors h-18 lg:h-22">
        <div className="container-landing h-full flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 focus:outline-none group cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
            >
              <img
                src="/logo.png"
                alt="Medikei"
                className="h-8 sm:h-9 w-auto dark:brightness-110 drop-shadow-xs"
              />
            </Link>
          </div>

          <div className="flex items-center gap-2.5 sm:gap-3.5">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-full hover:bg-muted/60 border border-border/60"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Voltar ao Início</span>
            </Link>

            <button
              onClick={handlePrint}
              type="button"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-full hover:bg-muted/60 border border-border/60"
              title="Imprimir documento ou salvar como PDF"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Imprimir / PDF</span>
            </button>

            <ThemeToggle />

            <Link to="/login" className="hidden sm:inline-block">
              <Button size="sm" variant="outline" className="rounded-full text-xs font-semibold px-4">
                Entrar
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Header Contextual */}
      <section className="border-b border-border/80 bg-neutral-50/70 dark:bg-neutral-900/40 py-10 sm:py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20 mb-3 shadow-2xs">
            <Shield className="w-3.5 h-3.5" />
            <span>{badge}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground tracking-tight">
            {title}
          </h1>

          <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
            <span>Última atualização: {lastUpdated}</span>
            <span>•</span>
            <span>Medikei Saúde Digital</span>
          </p>
        </div>
      </section>

      {/* Grid Principal */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Mobile Accordion Table of Contents */}
        <div className="lg:hidden mb-8">
          <button
            type="button"
            onClick={() => setIsMobileTocOpen(!isMobileTocOpen)}
            className="w-full flex items-center justify-between p-4 rounded-2xl border border-border bg-card shadow-xs text-left font-bold text-sm"
          >
            <div className="flex items-center gap-2 text-foreground">
              <FileText className="w-4 h-4 text-primary" />
              <span>Índice do Documento ({tocItems.length} tópicos)</span>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                isMobileTocOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isMobileTocOpen && (
            <div className="mt-2 p-4 rounded-2xl border border-border bg-card space-y-2 text-sm shadow-md animate-in fade-in duration-200">
              {tocItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setIsMobileTocOpen(false)}
                  className={`block py-1.5 px-2.5 rounded-lg text-xs font-medium transition-colors ${
                    activeSection === item.id
                      ? 'bg-primary/10 text-primary font-bold'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {item.title}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Desktop Sticky Table of Contents */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 space-y-4">
            <div className="p-5 rounded-3xl border border-border/80 bg-card/80 dark:bg-neutral-900/60 backdrop-blur-md shadow-sm">
              <h3 className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-primary" />
                <span>Nesta Página</span>
              </h3>
              <nav className="space-y-1 text-xs">
                {tocItems.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={`block py-2 px-3 rounded-xl transition-all leading-snug ${
                      activeSection === item.id
                        ? 'bg-primary/10 text-primary font-bold shadow-2xs translate-x-1'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                    }`}
                  >
                    {item.title}
                  </a>
                ))}
              </nav>

              <div className="mt-5 pt-4 border-t border-border/60">
                <button
                  onClick={handlePrint}
                  type="button"
                  className="w-full py-2 px-3 rounded-xl border border-border/70 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex items-center justify-center gap-2"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Imprimir Documento</span>
                </button>
              </div>
            </div>
          </aside>

          {/* Conteúdo Legal */}
          <article className="lg:col-span-8 text-left space-y-8 text-sm sm:text-base leading-relaxed text-neutral-700 dark:text-neutral-300">
            {children}
          </article>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-10 border-t border-border bg-card text-muted-foreground text-xs mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Medikei" className="h-6 w-auto dark:brightness-110" />
            <span>&copy; {new Date().getFullYear()} Medikei Saúde Digital. Todos os direitos reservados.</span>
          </div>

          <div className="flex items-center gap-6">
            <Link to="/privacidade" className="hover:text-foreground transition-colors">
              Política de Privacidade
            </Link>
            <Link to="/termos" className="hover:text-foreground transition-colors">
              Termos de Uso
            </Link>
            <Link to="/" className="text-primary font-semibold hover:underline">
              Início
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
